import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WEEKDAYS } from '../utils/rosterDates';

/** Form fields for bulk allocation across week / month / custom. */
export default function RosterBulkAllocateFields({
  track, form, setForm, staffList, accent, range, previewCount, selected, toggleDay,
}) {
  return (
    <div className="p-5 space-y-4">
      <div className="space-y-1.5">
        <Label>{track.staffNoun.charAt(0).toUpperCase() + track.staffNoun.slice(1)} *</Label>
        <select
          className="w-full h-10 border rounded-md px-3 text-sm"
          value={form.staff_id}
          onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
          data-testid="roster-bulk-staff"
        >
          <option value="">Select {track.staffNoun}</option>
          {staffList.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label className="text-xs">Apply to</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {[
            { id: 'week', label: 'This week' },
            { id: 'month', label: 'This month' },
            { id: 'custom', label: 'Custom end' },
          ].map((s) => (
            <button key={s.id} type="button" onClick={() => setForm({ ...form, scope: s.id })}
              className={`text-xs px-3 py-1.5 rounded-full border ${form.scope === s.id ? 'text-white border-transparent' : 'bg-white'}`}
              style={form.scope === s.id ? { background: accent } : {}}>
              {s.label}
            </button>
          ))}
        </div>
        {form.scope === 'custom' && (
          <div className="mt-2 space-y-1">
            <Label className="text-xs">Until</Label>
            <Input type="date" value={form.to_date} min={range.fromIso}
              onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-1.5">{range.fromIso} → {range.toIso}</p>
      </div>

      <div>
        <Label className="text-xs">Weekdays</Label>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <button type="button" className="text-[11px] px-2 py-1 border rounded-full"
            onClick={() => setForm({ ...form, days_of_week: [0, 1, 2, 3, 4] })}>Mon–Fri</button>
          <button type="button" className="text-[11px] px-2 py-1 border rounded-full"
            onClick={() => setForm({ ...form, days_of_week: [0, 1, 2, 3, 4, 5, 6] })}>All days</button>
          {WEEKDAYS.map((d) => (
            <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
              className={`px-2.5 py-1 rounded-full text-[11px] border ${form.days_of_week.includes(d.v) ? 'text-white border-transparent' : 'bg-white'}`}
              style={form.days_of_week.includes(d.v) ? { background: accent } : {}}>
              {d.l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">Shift presets</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {(track.presets || []).map((p) => (
            <button key={p.id} type="button"
              onClick={() => setForm({ ...form, shift_start: p.start, shift_end: p.end, location: p.location })}
              className="text-[11px] px-2.5 py-1.5 rounded-full border hover:bg-zinc-50">
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Start</Label>
          <Input type="time" value={form.shift_start}
            onChange={(e) => setForm({ ...form, shift_start: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">End</Label>
          <Input type="time" value={form.shift_end}
            onChange={(e) => setForm({ ...form, shift_end: e.target.value })} />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Location</Label>
        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </div>

      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <input type="checkbox" checked={form.published}
          onChange={(e) => setForm({ ...form, published: e.target.checked })} />
        {track.bookingAware
          ? 'Publish immediately for online booking'
          : 'Publish immediately as confirmed duty'}
      </label>

      <p className="text-xs text-zinc-600 rounded-lg bg-zinc-50 border px-3 py-2">
        About <strong>{previewCount}</strong> shifts will be created
        {selected ? ` for ${selected.name}` : ''}. Same-time duplicates are skipped.
      </p>
    </div>
  );
}
